/* eslint-disable @typescript-eslint/no-explicit-any */

import { Box, Theme, Typography } from '@mui/material';

import { StructureType } from '../../../types/types';

type GroupItem = {
 value?: string;
 translation: string;
 inner?: { [key: string]: GroupItem };
};

export type Output = { [key: string]: { [key: string]: GroupItem } };

export function groupRowItems(
 structures: StructureType[],
 data: { [key: string]: any },
): Output {
 const result: Output = {};

 function processStructure(
  structure: StructureType[],
  currentData: any,
  parentPath: string = '',
 ): void {
  structure.forEach((item) => {
   const currentPath = parentPath ? `${parentPath}.${item.Name}` : item.Name;
   const group = item.Group || ''; // Use empty string if no group is specified

   if (!result[group]) {
    result[group] = {};
   }

   // Handle inner structures recursively
   if (item.InnerStructure && item.InnerStructure.length > 0) {
    const innerValue = currentData?.[item.Name];
    if (innerValue) {
     result[group][currentPath] = {
      translation: item.Translation,
      inner: {},
     };
     processStructure(item.InnerStructure, innerValue, currentPath);
    }
   } else {
    // Handle leaf nodes
    const value = currentData?.[item.Name];
    if (value !== undefined) {
     result[group][currentPath] = {
      value: String(value),
      translation: item.Translation,
     };
    }
   }
  });
 }

 processStructure(structures, data);
 return result;
}

type GroupedData = {
 [group: string]: {
  [key: string]: GroupItem;
 };
};

interface GroupedDataDisplayProps {
 data: GroupedData;
}

export const GroupDisplay = ({ data }: GroupedDataDisplayProps) => {
 const renderGroupItem = (items: {
  [key: string]: GroupItem;
 }): React.ReactNode =>
  Object.entries(items).map(([key, item]) => (
   <Box key={key}>
    <Box
     sx={{
      display: 'flex',
      gap: 1, //   alignItems: 'center',
      justifyContent: 'space-between',
     }}
    >
     <Typography
      component="span"
      sx={{
       fontWeight: 'medium',
       fontSize: !item.value ? 14 : 12,
       color: (theme: Theme) =>
        !item.value ? theme.palette.kemek.gray : 'inherit',
      }}
     >
      {item.translation}
     </Typography>
     {item.value && (
      <Typography sx={{ fontSize: 12, textAlign: 'right' }}>
       {item.value === 'null' ? '-' : item.value}
      </Typography>
     )}
    </Box>
    {item.inner && renderGroupItem(item.inner)}
   </Box>
  ));

 return (
  <Box sx={{}}>
   {Object.entries(data).map(([group, items]) => (
    <Box key={group} sx={{ mb: 3 }}>
     {group && (
      <Typography
       sx={{
        mb: 2,
        fontSize: 14,
        fontWeight: 'medium',
       }}
      >
       {group}
      </Typography>
     )}
     <Box
      sx={{
       display: 'flex',
       flexDirection: 'column',
       gap: '16px',
       border: (theme: Theme) =>
        `1px solid ${theme.palette.kemek.lightestGray}`,
       borderRadius: 1,
       p: 2,
      }}
     >
      {renderGroupItem(items)}
     </Box>
    </Box>
   ))}
  </Box>
 );
};
